import apiClient from './axios';

// Feature flags (frontend-only)
const ENABLE_ROI_HISTORY_API = (import.meta?.env?.VITE_ENABLE_ROI_HISTORY_API === 'true');
const ENABLE_ROI_PROCESS_API = (import.meta?.env?.VITE_ENABLE_ROI_PROCESS_API === 'true');

// User API
export const userApi = {
  // Get all users (always return an array)
  getUsers: async () => {
    const resp = await apiClient.get('/users?limit=1000');
    return Array.isArray(resp) ? resp : (Array.isArray(resp?.users) ? resp.users : []);
  },

  // Get user by ID
  getUserById: (userId) => apiClient.get(`/users/${userId}`),

  // Create new user
  createUser: (userData) => apiClient.post('/users', userData),

  // Update user
  updateUser: (userId, userData) => apiClient.put(`/users/${userId}`, userData),

  // Delete user
  deleteUser: (userId) => apiClient.delete(`/users/${userId}`),
};

// Investment API
export const investmentApi = {
  // Get all investments
  getInvestments: () => apiClient.get('/investments'),

  // Get investment by ID
  getInvestmentById: (investmentId) => apiClient.get(`/investments/${investmentId}`),

  // Get investments by user ID
  getInvestmentsByUserId: (userId) => apiClient.get(`/investments/user/${userId}`),

  // Create new investment
  createInvestment: (investmentData) => apiClient.post('/investments', investmentData),

  // Update investment
  updateInvestment: (investmentId, investmentData) => apiClient.put(`/investments/${investmentId}`, investmentData),

  // Delete investment
  deleteInvestment: (investmentId) => apiClient.delete(`/investments/${investmentId}`),

  // Process ROI payment
  processROIPayment: (investmentId) => apiClient.post(`/investments/${investmentId}/roi`),

  // Process manual ROI (admin trigger - now using new Daily ROI API)
  processManualROI: () => apiClient.post('/daily-roi/process-daily'),

  // Get ROI history (guarded: returns [] if backend route not available)
  getROIHistory: async (month, year) => {
    const m = String(parseInt(month, 10) || '');
    const y = String(parseInt(year, 10) || '');
    if (!ENABLE_ROI_HISTORY_API) {
      return [];
    }
    // Prefer params to build query string safely
    return apiClient.get('/investments/roi-history', { params: { month: m, year: y } });
  },

  // Process bulk ROI (guarded: simulate success if backend route not available)
  processBulkROI: async (data) => {
    if (!ENABLE_ROI_PROCESS_API) {
      const calc = Array.isArray(data?.roiCalculations) ? data.roiCalculations : [];
      const totalAmount = calc.reduce((sum, r) => sum + (Number(r.roiAmount) || 0), 0);
      return {
        successfulPayouts: calc.length,
        failedPayouts: 0,
        totalAmount,
        processedAt: new Date().toISOString(),
      };
    }
    return apiClient.post('/investments/process-roi', data);
  },
};

// Transaction API
export const transactionApi = {
  // Get all transactions
  getTransactions: () => apiClient.get('/transactions'),

  // Get transaction by ID
  getTransactionById: (transactionId) => apiClient.get(`/transactions/${transactionId}`),

  // Get transactions by user ID
  getTransactionsByUserId: (userId) => apiClient.get(`/transactions/user/${userId}`),

  // Create new transaction
  createTransaction: (transactionData) => apiClient.post('/transactions', transactionData),

  // Update transaction
  updateTransaction: (transactionId, transactionData) => apiClient.put(`/transactions/${transactionId}`, transactionData),

  // Approve transaction
  approveTransaction: (transactionId) => apiClient.post(`/transactions/${transactionId}/approve`),

  // Reject transaction
  rejectTransaction: (transactionId, reason) => apiClient.post(`/transactions/${transactionId}/reject`, { reason }),
};

// Bank Details API
export const bankApi = {
  // Get all bank details
  getBankDetails: () => apiClient.get('/bank-details'),

  // Get bank details by ID
  getBankDetailById: (bankId) => apiClient.get(`/bank-details/${bankId}`),

  // Get bank details by user ID
  getBankDetailsByUserId: (userId) => apiClient.get(`/bank-details/user/${userId}`),

  // Create new bank detail
  createBankDetail: (bankData) => apiClient.post('/bank-details', bankData),

  // Update bank detail
  updateBankDetail: (bankId, bankData) => apiClient.put(`/bank-details/${bankId}`, bankData),

  // Delete bank detail
  deleteBankDetail: (bankId) => apiClient.delete(`/bank-details/${bankId}`),

  // Set primary bank account
  setPrimaryBankAccount: (bankId) => apiClient.post(`/bank-details/${bankId}/primary`),

  // Upload bank documents (multipart)
  uploadBankDocuments: (bankId, formData) => apiClient.post(`/bank-details/${bankId}/documents`, formData),
  setDocumentUrls: (bankId, documents) => apiClient.post(`/bank-details/${bankId}/document-urls`, { documents }),
};

// KYC API
export const kycApi = {
  // Get all KYC records
  getKycRecords: () => apiClient.get('/kyc'),

  // Get KYC record by ID
  getKycRecordById: (kycId) => apiClient.get(`/kyc/${kycId}`),

  // Get KYC record by user ID
  getKycRecordByUserId: (userId) => apiClient.get(`/kyc/user/${userId}`),

  // Create/update KYC record (self-service user)
  updateKycRecord: (kycData) => apiClient.post('/kyc', kycData),

  // Admin: create/update KYC for any user via email/userId (multipart)
  createKycAdmin: (formData) => apiClient.post('/kyc/admin', formData),

  // Approve KYC
  approveKyc: (kycId) => apiClient.post(`/kyc/${kycId}/approve`),

  // Reject KYC
  rejectKyc: (kycId, reason) => apiClient.post(`/kyc/${kycId}/reject`, { reason }),

  // Delete KYC (admin only)
  deleteKyc: (kycId) => apiClient.delete(`/kyc/${kycId}`),
};

export const storageApi = {
  presignPut: ({ folder, filename, contentType, size }) => apiClient.post('/storage/presign-put', { folder, filename, contentType, size }),
  getPublicUrl: (key) => apiClient.get('/storage/public-url', { params: { key } }),
  getCorsTemplate: () => apiClient.get('/storage/cors-template'),
};

// Notification API
export const notificationApi = {
  // Get all notifications
  getNotifications: () => apiClient.get('/notifications'),

  // Get notification by ID
  getNotificationById: (notificationId) => apiClient.get(`/notifications/${notificationId}`),

  // Create new notification
  createNotification: (notificationData) => apiClient.post('/notifications', notificationData),

  // Mark notification as read
  markAsRead: (notificationId) => apiClient.post(`/notifications/${notificationId}/read`),

  // Mark notification as unread
  markAsUnread: (notificationId) => apiClient.post(`/notifications/${notificationId}/unread`),

  // Delete notification
  deleteNotification: (notificationId) => apiClient.delete(`/notifications/${notificationId}`),

  // Bulk delete notifications (admin)
  deleteNotificationsBulk: (ids) => apiClient.post('/notifications/bulk-delete', { ids }),
};

// Investment Plan API
export const planApi = {
  // Get all investment plans (active only, public)
  getPlans: () => apiClient.get('/plans'),

  // Admin: Get all plans including inactive (requires auth)
  getAllPlansAdmin: () => apiClient.get('/plans/all'),

  // Get plan by ID
  getPlanById: (planId) => apiClient.get(`/plans/${planId}`),

  // Create new plan
  createPlan: (planData) => apiClient.post('/plans', planData),

  // Update plan
  updatePlan: (planId, planData) => apiClient.put(`/plans/${planId}`, planData),

  // Delete plan
  deletePlan: (planId) => apiClient.delete(`/plans/${planId}`),
};

// Gold Rate API - Legacy endpoints (backward compatible, read-only)
// Note: Manual rate updates are deprecated. Use goldPriceApi.getCurrentPrice() to fetch fresh rates.
export const goldRateApi = {
  // Get current gold rate from database (legacy endpoint)
  getCurrentRate: () => apiClient.get('/gold-rates'),

  // Get gold rate history
  getRateHistory: (days = 30) => apiClient.get(`/gold-rates/history?days=${days}`),

  // DEPRECATED: Manual rate updates are no longer supported
  // Use goldPriceApi.getCurrentPrice() instead to fetch fresh rates from external APIs
};

// NEW: Clean Gold Price API (pure spot price, no duties)
export const goldPriceApi = {
  // Get fresh price from external APIs (admin only)
  getCurrentPrice: () => apiClient.get('/gold-price'),

  // Get latest from database (public, fast)
  getLatestPrice: () => apiClient.get('/gold-price/latest'),

  // Get price history
  getHistory: (days = 30) => apiClient.get(`/gold-price/history?days=${days}`),

  // Clear cache (admin only)
  clearCache: () => apiClient.post('/gold-price/clear-cache'),
};


// Referral API
export const referralApi = {
  // Get referral data (optionally scoped to a userId)
  getReferralData: (userId) => userId
    ? apiClient.get('/referrals', { params: { userId } })
    : apiClient.get('/referrals'),

  // Get referral stats (optionally scoped to a userId)
  getReferralStats: (userId) => userId
    ? apiClient.get('/referrals/stats', { params: { userId } })
    : apiClient.get('/referrals/stats'),

  // Get referral history (optionally scoped to a userId)
  getReferralHistory: (userId) => userId
    ? apiClient.get('/referrals/history', { params: { userId } })
    : apiClient.get('/referrals/history'),

  // Generate referral code
  generateReferralCode: () => apiClient.post('/referrals/generate'),

  // Apply referral code
  applyReferralCode: (code) => apiClient.post('/referrals/apply', { code }),

  // Get referral earnings (optionally scoped to a userId)
  getReferralEarnings: (userId) => userId
    ? apiClient.get('/referrals/earnings', { params: { userId } })
    : apiClient.get('/referrals/earnings'),
};

// Dashboard Stats API
export const dashboardApi = {
  // Get admin dashboard stats
  getAdminStats: () => apiClient.get('/dashboard/admin'),
  // Get user dashboard stats
  getUserStats: (userId) => apiClient.get(`/dashboard/user/${userId}`),

  // Get recent activity
  getRecentActivity: (limit = 10) => apiClient.get(`/dashboard/activity?limit=${limit}`),

  // Bootstrap combined payload to reduce initial calls
  bootstrap: (limit = 5) => apiClient.get(`/dashboard/bootstrap`, { params: { limit } })
};

// Settings API (branding + key-value store)
export const settingsApi = {
  // Public: get branding
  getBranding: () => apiClient.get('/settings/branding'),
  // Admin: update branding (multipart)
  updateBranding: ({ panelName, logoFile, faviconFile }) => {
    const form = new FormData();
    if (panelName !== undefined) form.append('panelName', panelName);
    if (faviconFile) form.append('favicon', faviconFile);
    if (logoFile) form.append('logo', logoFile);
    return apiClient.put('/settings/branding', form);
  },
  // Generic key-value settings (admin only)
  getKeyValue: (key) => apiClient.get(`/settings/${encodeURIComponent(key)}`),
  setKeyValue: (key, value) => apiClient.put(`/settings/${encodeURIComponent(key)}`, { value }),
  getAll: () => apiClient.get('/settings/all'),
  // Notification channel helpers (admin only)
  getNotificationEmailTemplates: () =>
    apiClient.get('/settings/notification-channel/email/templates'),
};

// Audit API (admin only)
export const auditApi = {
  // Get audit logs
  getAuditLogs: (params = {}) => apiClient.get('/audit', { params }),

  // Get audit log by ID
  getAuditLogById: (id) => apiClient.get(`/audit/${id}`),

  // Get audit logs by user ID
  getAuditLogsByUserId: (userId, params = {}) => apiClient.get(`/audit/user/${userId}`, { params }),

  // Get audit logs by action
  getAuditLogsByAction: (action, params = {}) => apiClient.get(`/audit/action/${action}`, { params }),

  // Get audit logs by severity
  getAuditLogsBySeverity: (severity, params = {}) => apiClient.get(`/audit/severity/${severity}`, { params }),

  // Get audit log statistics
  getAuditLogStats: () => apiClient.get('/audit/stats/summary'),
};

// Session API (admin only)
export const sessionApi = {
  // Get current session info
  getCurrentSession: () => apiClient.get('/session/current'),

  // Get session history
  getSessionHistory: (params = {}) => apiClient.get('/session/history', { params }),

  // Force logout session
  forceLogout: (sessionId) => apiClient.post(`/session/${sessionId}/logout`),

  // Get active sessions
  getActiveSessions: () => apiClient.get('/session/active'),
};

// WhatsApp API (admin-only helpers for testing real config)
export const whatsappApi = {
  testConfig: () => apiClient.post('/whatsapp/test-config'),
  sendTestMessage: (payload) => apiClient.post('/whatsapp/send-test', payload),
};

// Daily ROI API
export const dailyROIApi = {
  // Process daily ROI (Admin)
  processDailyROI: () => apiClient.post('/daily-roi/process-daily'),

  // Backfill ROI for past dates (Admin)
  backfillDailyROI: (startDate, endDate) => apiClient.post('/daily-roi/backfill', { startDate, endDate }),

  // Process monthly maturity (Admin)
  processMonthlyMaturity: () => apiClient.post('/daily-roi/process-maturity'),

  // Get daily ROI history for an investment
  getDailyROIHistory: (investmentId, params = {}) => apiClient.get(`/daily-roi/history/${investmentId}`, { params }),

  // Get user's daily ROI history
  getUserDailyROIHistory: (params = {}) => apiClient.get('/daily-roi/user/history', { params }),

  // Get daily ROI stats (Admin)
  getDailyROIStats: () => apiClient.get('/daily-roi/stats'),
};

// Withdrawal API
export const withdrawalApi = {
  // Request withdrawal
  requestWithdrawal: (data) => apiClient.post('/withdrawals/request', data),

  // Get my withdrawals
  getMyWithdrawals: (params = {}) => apiClient.get('/withdrawals/my-requests', { params }),

  // Get all withdrawals (Admin)
  getAllWithdrawals: (params = {}) => apiClient.get('/withdrawals/all', { params }),

  // Approve withdrawal (Admin)
  approveWithdrawal: (id, data) => apiClient.put(`/withdrawals/${id}/approve`, data),

  // Reject withdrawal (Admin)
  rejectWithdrawal: (id, data) => apiClient.put(`/withdrawals/${id}/reject`, data),

  // Complete withdrawal (Admin)
  completeWithdrawal: (id, data) => apiClient.put(`/withdrawals/${id}/complete`, data),
};

// Reinvestment API
export const reinvestmentApi = {
  // Create reinvestment
  createReinvestment: (data) => apiClient.post('/reinvestments/create', data),

  // Get my reinvestments
  getMyReinvestments: (params = {}) => apiClient.get('/reinvestments/my-reinvestments', { params }),

  // Get all reinvestments (Admin)
  getAllReinvestments: (params = {}) => apiClient.get('/reinvestments/all', { params }),
};

// Settlement API
export const settlementApi = {
  // ==================== ADMIN ENDPOINTS ====================

  // Create monthly settlements for all eligible users
  createMonthlySettlements: (month, year) =>
    apiClient.post('/settlements/create', { month, year }),

  // Process single settlement (move pending → matured)
  processSettlement: (settlementId) =>
    apiClient.post(`/settlements/${settlementId}/process`),

  // Bulk process multiple settlements
  bulkProcessSettlements: (settlementIds) =>
    apiClient.post('/settlements/bulk-process', { settlementIds }),

  // Get all settlements with filters
  getSettlements: (filters = {}) =>
    apiClient.get('/settlements', { params: filters }),

  // Get settlement by ID
  getSettlementById: (settlementId) =>
    apiClient.get(`/settlements/${settlementId}`),

  // Approve settlement
  approveSettlement: (settlementId) =>
    apiClient.put(`/settlements/${settlementId}/approve`),

  // Reject settlement
  rejectSettlement: (settlementId, reason) =>
    apiClient.put(`/settlements/${settlementId}/reject`, { reason }),

  // Get settlement statistics
  getSettlementStats: (filters = {}) =>
    apiClient.get('/settlements/stats', { params: filters }),

  // Legacy: Process monthly settlement (create + auto-process)
  processMonthlySettlement: (month, year) =>
    apiClient.post('/settlements/process', { month, year }),

  // Legacy: Get settlement history
  getSettlementHistory: (params = {}) =>
    apiClient.get('/settlements/history', { params }),

  // ==================== USER ENDPOINTS ====================

  // Get user's settlements
  getMySettlements: (filters = {}) =>
    apiClient.get('/settlements/my-settlements', { params: filters }),

  // Withdraw settled ROI
  withdrawSettlement: (settlementId, bankDetails) =>
    apiClient.post(`/settlements/${settlementId}/withdraw`, { bankDetails }),

  // Reinvest settled ROI
  reinvestSettlement: (settlementId, planId) =>
    apiClient.post(`/settlements/${settlementId}/reinvest`, { planId }),
};
